class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
    
    try {
      this.barType = this.dataset.barType || 'single';
      this.enableConfetti = this.dataset.enableConfetti === 'true';
      this.currentTier = parseInt(this.dataset.currentTier) || 0;
      this.previousTier = this.currentTier;
      this.highestTierReached = this.currentTier;
      this.confettiTriggeredForTiers = new Set(); // Track which tiers have triggered confetti
      
      if (this.barType === 'tiered') {
        this.tier1Threshold = parseInt(this.dataset.tier1) || 0;
        this.tier2Threshold = parseInt(this.dataset.tier2) || 0;
        this.tier3Threshold = parseInt(this.dataset.tier3) || 0;
      } else {
        this.threshold = parseInt(this.dataset.freeShippingThreshold) || 10000;
      }
      
      this.progressFill = this.querySelector('.free-shipping-bar__progress-fill');
      this.message = this.querySelector('.free-shipping-bar__message');
      this.progressBar = this.querySelector('.free-shipping-bar__progress-bar');
      this.tierMarkers = this.querySelectorAll('.tier-marker');
      this.progressText = this.querySelector('.free-shipping-bar__progress-text');
      
      // Initialize confetti if enabled
      if (this.enableConfetti) {
        this.initConfetti();
      }
      
      // Store initial cart total if available
      this.lastCartTotal = null;
      
      this.init();
    } catch (error) {
      console.error('FreeShippingBar: Error in constructor:', error);
    }
  }

  initConfetti() {
    this.confettiColors = [
      '#FFD700', '#FFA500', '#FF69B4', '#00FF00', '#00CED1', '#FF1493'
    ];
    this.confettiCanvas = document.getElementById('confetti-canvas');
    
    if (this.confettiCanvas) {
      this.ctx = this.confettiCanvas.getContext('2d');
      this.confettiParticles = [];
      this.confettiAnimation = null;
      this.isConfettiActive = false;
      
      // Set canvas size
      this.resizeConfettiCanvas();
      window.addEventListener('resize', () => this.resizeConfettiCanvas());
    }
  }

  resizeConfettiCanvas() {
    if (this.confettiCanvas) {
      this.confettiCanvas.width = window.innerWidth;
      this.confettiCanvas.height = window.innerHeight;
    }
  }

  init() {
    try {
      // Check if subscribe function exists (from your theme)
      if (typeof subscribe !== 'undefined' && PUB_SUB_EVENTS) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.cartData && !event.cartData.errors) {
            this.updateFreeShippingBar(event);
          }
        });
      } else {
        // Fallback: Listen for cart updates via AJAX
        document.addEventListener('cart:updated', (event) => {
          if (event.detail?.cartData) {
            this.updateFreeShippingBar({ cartData: event.detail.cartData });
          }
        });
      }
      
      // Initial update if cart data is available
      this.fetchInitialCartData();
    } catch (error) {
      console.error('FreeShippingBar: Error in init:', error);
    }
  }
  
  fetchInitialCartData() {
    // Try to get cart data from various sources
    const cartData = window.cart || window.theme?.cart;
    if (cartData?.total_price) {
      this.updateFreeShippingBar({ cartData });
    }
  }

  disconnectedCallback() {
    try {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.confettiAnimation) {
        cancelAnimationFrame(this.confettiAnimation);
      }
    } catch (error) {
      console.error('FreeShippingBar: Error in disconnectedCallback:', error);
    }
  }

    async updateFreeShippingBar() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      const cartTotal = cartData.total_price ?? 0;

      if (this.barType === 'tiered') {
        this.updateTieredBar(cartTotal);
      } else {
        this.updateSingleBar(cartTotal);
      }
    } catch (error) {
      console.error('FreeShippingBar: cart fetch failed', error);
    }
  }


  updateTieredBar(cartTotal) {
    // Determine current tier based on cart total
    let newTier = 0;
    if (cartTotal >= this.tier3Threshold) {
      newTier = 3;
    } else if (cartTotal >= this.tier2Threshold) {
      newTier = 2;
    } else if (cartTotal >= this.tier1Threshold) {
      newTier = 1;
    }

    // Check if we're moving in positive direction (cart total increased)
    const isPositiveDirection = previousTotal !== null && cartTotal > previousTotal;
    
    // Store previous tier for comparison
    this.previousTier = this.currentTier;
    this.currentTier = newTier;
    
    // Update highest tier reached (only when moving forward)
    if (newTier > this.highestTierReached) {
      this.highestTierReached = newTier;
    }
    
    // Reset highest tier if cart is cleared or drops significantly
    if (cartTotal === 0) {
      this.highestTierReached = 0;
      this.confettiTriggeredForTiers.clear(); // Reset confetti tracking
    }

    // Calculate progress percentage based on max tier
    const progressPercentage = Math.min((cartTotal / this.tier3Threshold) * 100, 100);
    
    // Update progress bar width and tier styling
    this.updateTieredProgressBar(progressPercentage, this.currentTier);
    
    // Update markers
    this.updateTierMarkers(this.currentTier);
    
    // Update progress text
    this.updateProgressText(cartTotal);
    
    // Update message
    if (this.currentTier === 3) {
      this.showTierQualifiedMessage(3);
    } else {
      const nextThreshold = this.getNextThreshold(this.currentTier);
      const remainingAmount = Math.max(0, nextThreshold - cartTotal);
      this.showTierProgressMessage(this.currentTier, remainingAmount);
    }
    
    // Trigger confetti ONLY when:
    // 1. We've reached a new tier
    // 2. It's in positive direction (cart increased)
    // 3. We haven't already triggered confetti for this tier in this session
    // 4. The tier is actually achieved (not just maintained)
    if (
      this.enableConfetti &&
      newTier > this.previousTier && // Tier increased
      isPositiveDirection && // Cart total increased
      !this.confettiTriggeredForTiers.has(newTier) && // Not triggered before
      newTier > 0 // Valid tier
    ) {
      this.triggerConfetti(newTier);
      this.animateTierUnlock(newTier);
      this.confettiTriggeredForTiers.add(newTier);
    }
    
    // If cart total decreased below a tier threshold, remove that tier from triggered set
    // This allows re-triggering if the user adds items again later
    if (previousTotal !== null && cartTotal < previousTotal) {
      for (let tier = 1; tier <= 3; tier++) {
        const tierThreshold = this.getTierThreshold(tier);
        if (cartTotal < tierThreshold && this.confettiTriggeredForTiers.has(tier)) {
          this.confettiTriggeredForTiers.delete(tier);
        }
      }
    }
  }
  
  getTierThreshold(tier) {
    switch(tier) {
      case 1: return this.tier1Threshold;
      case 2: return this.tier2Threshold;
      case 3: return this.tier3Threshold;
      default: return Infinity;
    }
  }
  
  updateProgressText(cartTotal) {
    if (!this.progressText) return;
    
    const formattedTotal = this.formatMoney(cartTotal);
    const maxThreshold = this.barType === 'tiered' ? this.tier3Threshold : this.threshold;
    const formattedMax = this.formatMoney(maxThreshold);
    
    this.progressText.innerHTML = `
      <span>Progress: ${formattedTotal} of ${formattedMax}</span>
      <span class="free-shipping-bar__progress-amount">${Math.min(Math.round((cartTotal / maxThreshold) * 100), 100)}%</span>
    `;
  }

  updateTieredProgressBar(percentage, tier) {
    if (this.progressFill) {
      // Ensure percentage is between 0 and 100
      const validPercentage = Math.max(0, Math.min(100, percentage));
      
      // Set width
      this.progressFill.style.width = `${validPercentage}%`;
      this.progressFill.dataset.progress = Math.round(validPercentage);
      this.progressFill.dataset.currentTier = tier;
      
      // Force reflow to ensure transition works
      void this.progressFill.offsetWidth;
    }
  }

  updateTierMarkers(tier) {
    this.tierMarkers.forEach((marker, index) => {
      const markerTier = index + 1;
      
      // Remove all tier classes
      marker.classList.remove('tier-marker--active', 'tier-marker--completed');
      
      if (markerTier <= tier) {
        marker.classList.add('tier-marker--active');
      }
      
      // Add completed class for tiers that are passed
      if (markerTier < tier) {
        marker.classList.add('tier-marker--completed');
      }
    });
  }

  getNextThreshold(currentTier) {
    switch(currentTier) {
      case 0: return this.tier1Threshold;
      case 1: return this.tier2Threshold;
      case 2: return this.tier3Threshold;
      default: return this.tier3Threshold;
    }
  }

  showTierQualifiedMessage(tier) {
    try {
      if (!this.message) return;

      let successMessage = '';
      switch(tier) {
        case 1:
          successMessage = window.freeShippingStrings?.tier_1_success || "🎉 Tier 1 Unlocked: <strong>Free Shipping</strong>";
          break;
        case 2:
          successMessage = window.freeShippingStrings?.tier_2_success || "🎉 Tier 2 Unlocked: <strong>Free Gift</strong>";
          break;
        case 3:
          successMessage = window.freeShippingStrings?.tier_3_success || "🎉 All Tiers Unlocked! <strong>Maximum Rewards!</strong>";
          break;
      }

      this.message.classList.add('free-shipping-bar__message--success');
      this.message.innerHTML = `
        <span class="free-shipping-bar__text rte qualified">
          ${successMessage}
        </span>
      `;
    } catch (error) {
      console.error('FreeShippingBar: Error showing qualified message:', error);
    }
  }

  showTierProgressMessage(tier, remainingAmount) {
    try {
      if (!this.message) return;

      this.message.classList.remove('free-shipping-bar__message--success');
      
      let progressMessage = '';
      switch(tier) {
        case 0:
          progressMessage = window.freeShippingStrings?.tier_1_progress || "You're {{ amount }} away from <strong>Free Shipping</strong>";
          break;
        case 1:
          progressMessage = window.freeShippingStrings?.tier_2_progress || "You're {{ amount }} away from <strong>Free Gift</strong>";
          break;
        case 2:
          progressMessage = window.freeShippingStrings?.tier_3_progress || "You're {{ amount }} away from <strong>Free Express Shipping</strong>";
          break;
      }

      const formattedMessage = this.formatMessage(progressMessage, remainingAmount);
      
      this.message.innerHTML = `
        <span class="free-shipping-bar__text rte progress">
          ${formattedMessage}
        </span>
      `;
    } catch (error) {
      console.error('FreeShippingBar: Error showing progress message:', error);
    }
  }

  updateSingleBar(cartTotal, previousTotal) {
    const remainingAmount = Math.max(0, this.threshold - cartTotal);
    const progressPercentage = Math.min((cartTotal / this.threshold) * 100, 100);

    this.updateProgressBar(progressPercentage);
    this.updateProgressText(cartTotal);

    // Check if we just reached the threshold
    const justReachedThreshold = previousTotal !== null && 
                                 previousTotal < this.threshold && 
                                 cartTotal >= this.threshold;

    if (remainingAmount <= 0) {
      this.showQualifiedMessage();
      
      // Trigger confetti only when just reaching threshold in positive direction
      if (this.enableConfetti && justReachedThreshold && cartTotal > previousTotal) {
        this.triggerConfetti(1);
      }
    } else {
      this.showProgressMessage(remainingAmount);
    }
  }

  updateProgressBar(percentage) {
    if (this.progressFill) {
      const validPercentage = Math.max(0, Math.min(100, percentage));
      this.progressFill.style.width = `${validPercentage}%`;
      this.progressFill.dataset.progress = Math.round(validPercentage);
    }
  }

  showQualifiedMessage() {
    try {
      if (!this.message) return;

      this.message.classList.add('free-shipping-bar__message--success');
      this.message.innerHTML = `
        <span class="free-shipping-bar__text rte qualified">
          ${this.getQualifiedMessage()}
        </span>
      `;
    } catch (error) {
      console.error('FreeShippingBar: Error showing qualified message:', error);
    }
  }

  showProgressMessage(remainingAmount) {
    try {
      if (!this.message) return;

      this.message.classList.remove('free-shipping-bar__message--success');
      this.message.innerHTML = `
        <span class="free-shipping-bar__text rte progress">
          ${this.formatMessage(this.getProgressMessage(), remainingAmount)}
        </span>
      `;
    } catch (error) {
      console.error('FreeShippingBar: Error showing progress message:', error);
    }
  }

  getQualifiedMessage() {
    return window.freeShippingStrings?.free_shipping_qualified || 
           "🎉 Congrats! You've unlocked <strong>Free Gift</strong>";
  }

  getProgressMessage() {
    return window.freeShippingStrings?.free_shipping_progress || 
           "You're only {{ amount }} away from <strong>Free Gift</strong>";
  }

  animateTierUnlock(tier) {
    if (this.progressFill) {
      this.progressFill.classList.add('tier-unlock-animation');
      setTimeout(() => {
        this.progressFill.classList.remove('tier-unlock-animation');
      }, 500);
    }
    
    // Also animate the tier marker
    const targetMarker = this.tierMarkers[tier - 1];
    if (targetMarker) {
      targetMarker.classList.add('tier-unlock-animation');
      setTimeout(() => {
        targetMarker.classList.remove('tier-unlock-animation');
      }, 500);
    }
  }

  triggerConfetti(tier) {
    // Don't trigger if confetti is already active
    if (this.isConfettiActive) return;
    
    if (!this.confettiCanvas || !this.ctx) return;
    
    // Clear previous animation
    if (this.confettiAnimation) {
      cancelAnimationFrame(this.confettiAnimation);
    }
    
    this.isConfettiActive = true;
    this.confettiCanvas.style.display = 'block';
    this.confettiParticles = [];
    
    // Create confetti particles based on tier (more particles for higher tiers)
    const particleCount = Math.min(tier * 75, 200); // Cap at 200 particles
    
    for (let i = 0; i < particleCount; i++) {
      this.confettiParticles.push({
        x: Math.random() * this.confettiCanvas.width,
        y: Math.random() * this.confettiCanvas.height - this.confettiCanvas.height,
        size: Math.random() * 10 + 4,
        speed: Math.random() * 6 + 2,
        angle: Math.random() * Math.PI * 2,
        color: this.confettiColors[Math.floor(Math.random() * this.confettiColors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 8,
        swing: Math.random() * 2 - 1,
        swingSpeed: Math.random() * 0.02
      });
    }
    
    const animate = () => {
      this.ctx.clearRect(0, 0, this.confettiCanvas.width, this.confettiCanvas.height);
      
      let stillActive = false;
      
      this.confettiParticles.forEach((particle, i) => {
        // Move particle
        particle.y += particle.speed;
        particle.x += Math.sin(particle.angle) * particle.swing;
        particle.rotation += particle.rotationSpeed;
        particle.angle += particle.swingSpeed;
        
        // Check if still on screen
        if (particle.y < this.confettiCanvas.height + 50) {
          stillActive = true;
        }
        
        // Draw particle
        this.ctx.save();
        this.ctx.translate(particle.x, particle.y);
        this.ctx.rotate((particle.rotation * Math.PI) / 180);
        this.ctx.fillStyle = particle.color;
        this.ctx.shadowColor = 'rgba(0,0,0,0.2)';
        this.ctx.shadowBlur = 4;
        this.ctx.shadowOffsetX = 1;
        this.ctx.shadowOffsetY = 1;
        
        // Draw different shapes for variety
        if (i % 3 === 0) {
          // Circle
          this.ctx.beginPath();
          this.ctx.arc(0, 0, particle.size / 2, 0, Math.PI * 2);
          this.ctx.fill();
        } else {
          // Rectangle
          this.ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        }
        
        this.ctx.restore();
      });
      
      if (stillActive) {
        this.confettiAnimation = requestAnimationFrame(animate);
      } else {
        this.confettiCanvas.style.display = 'none';
        this.isConfettiActive = false;
      }
    };
    
    animate();
    
    // Auto hide after 5 seconds
    setTimeout(() => {
      if (this.confettiAnimation) {
        cancelAnimationFrame(this.confettiAnimation);
        this.confettiCanvas.style.display = 'none';
        this.isConfettiActive = false;
      }
    }, 5000);
  }

  formatMessage(template, amount) {
    try {
      if (!template) return '';
      return template.replace(/\{\{\s*amount\s*\}\}/g, this.formatMoney(amount));
    } catch (error) {
      console.error('FreeShippingBar: Error formatting message:', error);
      return template || '';
    }
  }

  formatMoney(amount) {
    try {
      if (typeof amount !== 'number' || isNaN(amount) || amount < 0) {
        return '$0.00';
      }
      
      const dollars = (amount / 100).toFixed(2);
      return `$${dollars}`;
    } catch (error) {
      console.error('FreeShippingBar: Error formatting money:', error);
      return '$0.00';
    }
  }
}

// Ensure the custom element is defined
if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}